import type { JSX } from 'react';
import { IconDownload } from '../ui/icons.js';

// DXTECH CI(Corporate Identity) — 브랜드 설명 + 다양한 형태의 로고 파일 다운로드.
// 파일 원본: _CI/ (일관 네이밍) → apps/web/public/brand/ci/ 로 복사되어 정적 서빙됨.

const BRAND_RED = '#E51B23';
const CI = '/brand/ci';

interface AssetFile { fmt: 'PNG' | 'SVG' | 'PSD'; href: string }
interface Asset {
  label: string;
  note?: string;
  preview: string; // 미리보기 이미지 경로
  dark?: boolean; // 어두운 배경 타일에 표시(화이트 계열)
  contain?: boolean; // preview 를 tile 에 여백 포함해 맞춤
  files: AssetFile[];
}
interface Group { title: string; desc: string; assets: Asset[] }

const GROUPS: Group[] = [
  {
    title: '시그니처 · 가로형',
    desc: '심볼과 로고타입을 가로로 조합한 기본 시그니처입니다. 일반적인 문서·웹·명함 등 대부분의 매체에 우선 사용합니다.',
    assets: [
      { label: '기본형', note: '레드 심볼 + 블랙 타이포', preview: `${CI}/DXTECH_Signature_H.svg`, contain: true,
        files: [{ fmt: 'PNG', href: `${CI}/DXTECH_Signature_H.png` }, { fmt: 'SVG', href: `${CI}/DXTECH_Signature_H.svg` }, { fmt: 'PSD', href: `${CI}/DXTECH_Signature_H.psd` }] },
      { label: '텍스트 화이트', note: '어두운 배경용 · 타이포 화이트', preview: `${CI}/DXTECH_Signature_H_TextWhite.png`, dark: true, contain: true,
        files: [{ fmt: 'PNG', href: `${CI}/DXTECH_Signature_H_TextWhite.png` }, { fmt: 'SVG', href: `${CI}/DXTECH_Signature_H_TextWhite.svg` }] },
      { label: '반전 (화이트)', note: '전체 화이트 · 컬러/사진 배경용', preview: `${CI}/DXTECH_Signature_H_White.svg`, dark: true, contain: true,
        files: [{ fmt: 'SVG', href: `${CI}/DXTECH_Signature_H_White.svg` }] },
      { label: '배경 적용 예', note: '이미지 배경 위 사용 예시', preview: `${CI}/DXTECH_Signature_H_OnSky.png`,
        files: [{ fmt: 'PNG', href: `${CI}/DXTECH_Signature_H_OnSky.png` }] },
    ],
  },
  {
    title: '시그니처 · 세로형',
    desc: '가로 공간이 좁거나 정방형 레이아웃에서 사용하는 세로 조합형입니다.',
    assets: [
      { label: '기본형', note: '레드 심볼 + 블랙 타이포', preview: `${CI}/DXTECH_Signature_V.png`, contain: true,
        files: [{ fmt: 'PNG', href: `${CI}/DXTECH_Signature_V.png` }, { fmt: 'PSD', href: `${CI}/DXTECH_Signature_V.psd` }] },
    ],
  },
  {
    title: '심볼마크',
    desc: '가로줄 패턴의 육각 심볼입니다. 앱 아이콘·파비콘·워터마크 등 심볼 단독 사용 시 활용합니다.',
    assets: [
      { label: '레드', preview: `${CI}/DXTECH_Symbol.svg`, contain: true,
        files: [{ fmt: 'PNG', href: `${CI}/DXTECH_Symbol.png` }, { fmt: 'SVG', href: `${CI}/DXTECH_Symbol.svg` }, { fmt: 'PSD', href: `${CI}/DXTECH_Symbol.psd` }] },
      { label: '화이트', note: '어두운 배경용', preview: `${CI}/DXTECH_Symbol_White.svg`, dark: true, contain: true,
        files: [{ fmt: 'SVG', href: `${CI}/DXTECH_Symbol_White.svg` }] },
    ],
  },
  {
    title: '로고타입',
    desc: '“DXTECH” 워드마크 단독형입니다. 심볼과 분리해 사용해야 할 때 활용합니다.',
    assets: [
      { label: '블랙', preview: `${CI}/DXTECH_Wordmark_Black.svg`, contain: true,
        files: [{ fmt: 'SVG', href: `${CI}/DXTECH_Wordmark_Black.svg` }, { fmt: 'PSD', href: `${CI}/DXTECH_Wordmark.psd` }] },
      { label: '화이트', note: '어두운 배경용', preview: `${CI}/DXTECH_Wordmark_White.svg`, dark: true, contain: true,
        files: [{ fmt: 'SVG', href: `${CI}/DXTECH_Wordmark_White.svg` }] },
    ],
  },
];

// 컬러 팔레트
const PALETTE = [
  { name: 'DXTECH Red', hex: '#E51B23', sub: '주조색 (Primary)', light: false },
  { name: 'Ink Black', hex: '#1F2937', sub: '타이포·본문', light: false },
  { name: 'White', hex: '#FFFFFF', sub: '배경·반전', light: true },
];

// CI 상징 해설
const MEANINGS: { title: string; body: JSX.Element }[] = [
  { title: '오각형 (五角形): 오행(五行)', body: <>각 면의 오각형은 우주 만물의 변화를 <b>나무(木)·불(火)·흙(土)·쇠(金)·물(水)</b>의 다섯 가지 기운으로 나타냅니다.</> },
  { title: '12면: 화합(和合)', body: <>12개의 면은 <b>십이지(十二支) 12띠의 화합</b>, 즉 모든 사람들 간의 화합을 상징합니다.</> },
  { title: '붉은색 · 투명 라인: 음양(陰陽)', body: <>붉은색 라인과 투명 라인은 <b>음양의 조화</b>와 <b>디지털 세계</b>를 의미합니다.</> },
  { title: '레드 컬러: 역동성', body: <>붉은색은 <b style={{ color: BRAND_RED }}>젊고 역동적인 기운</b>을 상징합니다.</> },
];

// 로고타입(워드마크) 서체 규격
const TYPO = { font: 'Tahoma', weight: 'Regular', width: '120%' };

export function CompanyCI(): JSX.Element {
  return (
    <div style={{ maxWidth: 880, margin: '0 auto', animation: 'dxfade .3s ease', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 상단 CI 설명 히어로 */}
      <div className="dx-card" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 22, padding: '26px 24px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ width: 96, height: 96, flexShrink: 0, borderRadius: 18, background: '#fff', border: '1px solid #EEF0F3', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 18px rgba(16,24,40,.06)' }}>
            <img src={`${CI}/DXTECH_Symbol.svg`} alt="DXTECH 심볼" style={{ width: 64, height: 64 }} />
          </div>
          <div style={{ minWidth: 220, flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '.14em', color: BRAND_RED }}>CORPORATE IDENTITY</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, margin: '4px 0 4px', letterSpacing: '-.02em' }}>DXTECH C.I.</h2>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1F2937', marginBottom: 10 }}>
              DXTECH <span style={{ color: '#9CA3AF', fontWeight: 500 }}>=</span> <span style={{ color: BRAND_RED }}>D</span>igital <span style={{ color: BRAND_RED }}>T</span>ransformation <span style={{ color: BRAND_RED }}>Tech</span>nology
            </div>
            <p style={{ fontSize: 13.5, color: '#4B5563', lineHeight: 1.75, margin: 0 }}>
              DXTECH C.I.는 <b>오각형 12면</b>으로 구성된 <b>정십이면체(正十二面體)</b>를 형상화하였습니다.
            </p>
          </div>
        </div>

        {/* CI 의미 — 정십이면체 도해 + 상징 해설 */}
        <div style={{ borderTop: '1px solid #F3F4F6', padding: '18px 24px', display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
          <figure style={{ margin: 0, flex: '0 0 auto', textAlign: 'center' }}>
            <img src={`${CI}/DXTECH_Dodecahedron.svg`} alt="오각형 12면으로 구성된 정십이면체" style={{ width: 172, height: 164, display: 'block' }} />
            <figcaption style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>정십이면체 · 오각형 12면</figcaption>
          </figure>
          <div style={{ flex: '1 1 300px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '13px 22px' }}>
            {MEANINGS.map((m) => (
              <div key={m.title} style={{ display: 'flex', gap: 10 }}>
                <div style={{ width: 4, borderRadius: 2, background: BRAND_RED, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#1F2937', marginBottom: 3 }}>{m.title}</div>
                  <div style={{ fontSize: 12.5, color: '#4B5563', lineHeight: 1.6 }}>{m.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 로고타입 서체 규격 */}
        <div style={{ borderTop: '1px solid #F3F4F6', padding: '13px 24px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', color: '#6B7280' }}>로고타입 서체</span>
          <span style={{ fontFamily: 'Tahoma, Geneva, sans-serif', fontSize: 20, fontWeight: 400, transform: 'scaleX(1.2)', transformOrigin: 'left center', display: 'inline-block', color: '#1F2937', letterSpacing: '.02em' }}>DXTECH</span>
          <span style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginLeft: 'auto' }}>
            <Spec k="Font" v={TYPO.font} />
            <Spec k="Weight" v={TYPO.weight} />
            <Spec k="장평(W)" v={TYPO.width} />
          </span>
        </div>
        {/* 컬러 팔레트 */}
        <div style={{ borderTop: '1px solid #F3F4F6', padding: '16px 24px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {PALETTE.map((c) => (
            <div key={c.hex} style={{ display: 'flex', alignItems: 'center', gap: 11, flex: '1 1 200px', minWidth: 180 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: c.hex, border: c.light ? '1px solid #E5E7EB' : 'none', flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1F2937' }}>{c.name}</div>
                <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{c.hex} · {c.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 다운로드 안내 */}
      <div>
        <h3 style={{ fontSize: 17, fontWeight: 800, margin: '4px 0 3px' }}>로고 다운로드</h3>
        <div style={{ fontSize: 12.5, color: '#9CA3AF' }}>
          용도에 맞는 형태와 파일 형식을 선택해 내려받으세요. 인쇄·확대는 벡터(SVG), 원본 편집은 PSD를 권장합니다.
        </div>
      </div>

      {GROUPS.map((g) => (
        <div key={g.title} className="dx-card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '15px 18px 12px', borderBottom: '1px solid #F3F4F6' }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{g.title}</div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 3, lineHeight: 1.6 }}>{g.desc}</div>
          </div>
          <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12 }}>
            {g.assets.map((a) => (
              <AssetTile key={a.label} asset={a} />
            ))}
          </div>
        </div>
      ))}

      <div style={{ fontSize: 11, color: '#9CA3AF', lineHeight: 1.7, padding: '0 4px 8px' }}>
        ※ 로고는 원형·비율을 임의로 변형하거나 색상을 바꾸어 사용할 수 없습니다. 최소 여백을 확보하고, 배경과 충분한 대비를 유지해 주세요.
        추가 원본 파일(배경·아이콘 세트·월페이퍼 등)이 필요하면 경영지원팀에 문의하세요.
      </div>
    </div>
  );
}

// 서체 규격 칩 — 라벨 + 값
function Spec({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#F3F4F6', borderRadius: 8, padding: '4px 10px' }}>
      <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600 }}>{k}</span>
      <span style={{ fontSize: 12.5, color: '#1F2937', fontWeight: 700 }}>{v}</span>
    </span>
  );
}

function AssetTile({ asset }: { asset: Asset }): JSX.Element {
  return (
    <div style={{ border: '1px solid #EDEFF2', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        height: 116, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: asset.dark ? '#1F2430' : '#F8FAFC',
        borderBottom: '1px solid #EDEFF2', padding: asset.contain ? 18 : 0,
      }}>
        <img src={asset.preview} alt={asset.label}
          style={asset.contain
            ? { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }
            : { width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
      <div style={{ padding: '11px 13px', display: 'flex', flexDirection: 'column', gap: 9, flex: 1 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1F2937' }}>{asset.label}</div>
          {asset.note && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{asset.note}</div>}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 'auto' }}>
          {asset.files.map((f) => (
            <a key={f.fmt} href={f.href} download className="dx-btn dx-btn-ghost"
              style={{ padding: '5px 10px', fontSize: 12, fontWeight: 700, gap: 5, textDecoration: 'none' }}>
              <IconDownload size={13} />{f.fmt}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
